import { Check, Plus, Settings01, Tag01, XClose } from "@untitledui/icons";
import { memo, type ReactNode, useCallback, useMemo, useState } from "react";
import {
  useCreateAndAssignCreatorCohort,
  useCreatorCohorts,
  useSaveCreatorCohortAssignment,
} from "@/api/creator-cohorts";
import { EmptyState } from "@/components/application/empty-state/empty-state";
import { Command, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { randomTagColor } from "@/lib/creator-tags/colors";

// Mirrors the server-side limit (`createCohortSchema` in packages/api .../creatorCohorts/routes.ts).
const MAX_COHORT_NAME_LENGTH = 50;

type CohortOption = { id: string; name: string; color: string };

const CohortOptionRow = memo(
  ({ cohort, selected, onSelect }: { cohort: CohortOption; selected: boolean; onSelect: (id: string) => void }) => {
    const handleSelect = useCallback(() => onSelect(cohort.id), [onSelect, cohort.id]);

    return (
      <CommandItem value={cohort.id} onSelect={handleSelect} className="gap-2">
        <span className="size-2.5 shrink-0 rounded-full" style={{ backgroundColor: cohort.color }} />
        <span className="truncate">{cohort.name}</span>
        {selected && <Check className="ml-auto size-4 shrink-0 text-brand" />}
      </CommandItem>
    );
  },
);
CohortOptionRow.displayName = "CohortOptionRow";

export interface CohortPickerPopoverProps {
  creatorId: string;
  /** The creator's current cohort id, or null when unassigned. */
  assignedCohortId: string | null;
  onManageAllCohorts: () => void;
  children: ReactNode;
}

// Single-select: a creator has at most one cohort, so selecting a cohort assigns
// it (replacing any existing one) and commits immediately. Selecting the current
// cohort — or the explicit "No cohort" row — clears it.
export const CohortPickerPopover = ({
  creatorId,
  assignedCohortId,
  onManageAllCohorts,
  children,
}: CohortPickerPopoverProps) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const { data: allCohorts = [] } = useCreatorCohorts();
  const saveAssignment = useSaveCreatorCohortAssignment();
  const createAndAssign = useCreateAndAssignCreatorCohort(creatorId);

  const query = search.trim();
  const filtered = useMemo(() => {
    const lower = query.toLowerCase();
    return allCohorts.filter((cohort) => cohort.name.toLowerCase().includes(lower));
  }, [allCohorts, query]);

  // Only offer "Create" when the query doesn't exactly match an existing cohort
  // (case-insensitive), since duplicate names are rejected server-side.
  const hasExactMatch = useMemo(
    () => allCohorts.some((cohort) => cohort.name.toLowerCase() === query.toLowerCase()),
    [allCohorts, query],
  );
  const showCreate = query.length > 0 && !hasExactMatch;

  const assign = useCallback(
    (cohortId: string | null) => {
      // Selecting the already-assigned cohort clears it (toggle off).
      const next = cohortId === assignedCohortId ? null : cohortId;
      if (next !== assignedCohortId) {
        saveAssignment.mutate({ creatorIds: [creatorId], cohortId: next });
      }
      setOpen(false);
    },
    [assignedCohortId, creatorId, saveAssignment],
  );

  const handleCreate = useCallback(async () => {
    const name = query;
    if (!name) return;
    try {
      await createAndAssign.mutateAsync({ name, color: randomTagColor() });
      setSearch("");
      setOpen(false);
    } catch {
      // toast handled in the hook
    }
  }, [createAndAssign, query]);

  const handleOpenChange = useCallback((next: boolean) => {
    if (next) setSearch("");
    setOpen(next);
  }, []);

  return (
    // `modal` so an outside click is consumed to dismiss the picker and never
    // reaches the table row underneath (which would otherwise open the drawer).
    <Popover open={open} onOpenChange={handleOpenChange} modal>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent align="start" className="w-[260px] p-0">
        <Command shouldFilter={false}>
          <CommandInput
            autoFocus
            maxLength={MAX_COHORT_NAME_LENGTH}
            placeholder="Search or type to create a cohort..."
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            {!showCreate && filtered.length === 0 && (
              <EmptyState size="sm" className="py-6">
                <EmptyState.FeaturedIcon icon={Tag01} color="gray" />
                <EmptyState.Content>
                  <EmptyState.Title>No Cohorts</EmptyState.Title>
                  <EmptyState.Description>Start typing above to create a cohort</EmptyState.Description>
                </EmptyState.Content>
              </EmptyState>
            )}
            {showCreate && (
              <CommandGroup>
                <CommandItem value={`__create__${query}`} onSelect={handleCreate} className="gap-2">
                  <Plus className="size-4 shrink-0" />
                  <span className="truncate">
                    Create <span className="font-medium">“{query}”</span>
                  </span>
                </CommandItem>
              </CommandGroup>
            )}
            {assignedCohortId && (
              <CommandGroup>
                <CommandItem value="__none__" onSelect={() => assign(null)} className="gap-2">
                  <XClose className="size-4 shrink-0 text-quaternary" />
                  <span className="truncate text-tertiary">No cohort</span>
                </CommandItem>
              </CommandGroup>
            )}
            {filtered.length > 0 && (
              <CommandGroup>
                {filtered.map((cohort) => (
                  <CohortOptionRow
                    key={cohort.id}
                    cohort={cohort}
                    selected={cohort.id === assignedCohortId}
                    onSelect={assign}
                  />
                ))}
              </CommandGroup>
            )}
          </CommandList>
          {!(!showCreate && filtered.length === 0) && (
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                onManageAllCohorts();
              }}
              className="flex w-full items-center gap-2 border-t border-secondary px-3 py-2 text-sm text-tertiary hover:bg-secondary"
            >
              <Settings01 className="size-4 shrink-0" />
              Manage All Cohorts
            </button>
          )}
        </Command>
      </PopoverContent>
    </Popover>
  );
};
